import { partition } from "lodash";
import * as React from "react";

import { useUserConfig } from "@/shared/domain/config/hooks/useUserConfig";
import { LayoutRecord } from "@/shared/domain/layouts";
import { useLayouts } from "@/shared/domain/layouts/hooks";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";

export interface LayoutOption {
  id: string;
  record: LayoutRecord;
  isDefault: boolean;
}

interface LayoutOptionsResult {
  personalLayouts: LayoutOption[];
  teamLayouts: LayoutOption[];
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useLayoutOptions(): LayoutOptionsResult {
  const layoutsQuery = useLayouts();

  const usersDefaultLayoutQuery = useUserConfig<string>(
    "user_default_layout_id",
  );
  const defaultLayoutId = usersDefaultLayoutQuery.data ?? null;

  const [personalLayouts, teamLayouts] = React.useMemo(() => {
    if (!layoutsQuery.data) {
      return [[], []];
    }

    return partition(layoutsQuery.data, (layout) => {
      return layout.accessibility === LayoutAccessibility.User;
    });
  }, [layoutsQuery.data]);

  const layoutOptions = React.useMemo(() => {
    return {
      personalLayouts: toLayoutOptions(personalLayouts, defaultLayoutId),
      teamLayouts: toLayoutOptions(teamLayouts, defaultLayoutId),
    };
  }, [defaultLayoutId, personalLayouts, teamLayouts]);

  return {
    personalLayouts: layoutOptions.personalLayouts,
    teamLayouts: layoutOptions.teamLayouts,
    isPending: layoutsQuery.isPending,
    isError: layoutsQuery.isError,
    refetch: () => {
      void layoutsQuery.refetch();
    },
  };
}

function toLayoutOptions(
  layouts: LayoutRecord[],
  defaultLayoutId: string | null,
): LayoutOption[] {
  const options = layouts.map((layout) => ({
    id: layout.layout_id,
    record: layout,
    isDefault: defaultLayoutId === layout.layout_id,
  }));

  return options.sort((a, b) => {
    // Sort by name alphabetically
    return a.record.name.localeCompare(b.record.name);
  });
}
